
import { Component, Provide, Prop, Mixins } from "vue-property-decorator";
import { CreateElement } from "vue";

// Mixins
import { FilteringSortingTableStore } from "@/mixins/tableWithFilteringAndSortingStore";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { affiliatePayoutDestinationFilters } from "@/data/filters/affiliate";
import { affiliatePayoutDestinationSorters } from "@/data/sorters/affiliate";

// Typings
import {
  IAffiliatePayoutDestinationsProvider,
  IAffiliatePayoutDestinationsMethods
} from "@/models/providers/affiliatePayoutDestinations";
import {
  IAffiliatePayoutDestination,
  IAffiliateBrandPayoutDestination
} from "@/models/affiliate";
import { IBrand } from "@/models/brands";
import { AffiliatePayoutDestinationCode } from "@/data/enums/affiliates";

@Component
export default class PayoutDestinationsProvider extends Mixins(
  FilteringSortingTableStore
) {
  @Prop({ required: true })
  public brandId!: IBrand["id"];

  @Prop({ default: true })
  public handleLoading!: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  @Prop({ default: true })
  public handleRequestError!: boolean;

  @Prop({ default: "APDP1" })
  public declare id: string;

  // Data
  public dataModule: DataModules = DataModules.AFFILIATES_PAYOUT_DESTINATIONS;

  public filters = affiliatePayoutDestinationFilters;
  public sorters = affiliatePayoutDestinationSorters;

  // Lifecycle methods

  public async created() {
    if (!this.contentIsAvailable) return;
    this.getData();
  }

  // overrides mixin method
  public getStoreParams(params: any) {
    return this.$_.merge(params, {
      brandId: this.brandId
    });
  }

  public guardFunctionalities = [
    "list_affiliate_payout_brand_destinations",
    "list_affiliate_payout_brand_destinations_available"
  ];

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  // Computed

  public get canCreate() {
    return this.$userCan("create_affiliate_payout_brand_destination");
  }

  public get canUpdate() {
    return this.$userCan("update_affiliate_payout_brand_destination");
  }

  public get canDelete() {
    return this.$userCan("delete_affiliate_payout_brand_destination");
  }

  public get payoutDestinations() {
    return this.table.data as IAffiliateBrandPayoutDestination[];
  }

  public get defaultDestination() {
    return this.payoutDestinations?.find(i => i.default);
  }

  // Methods

  public isPaypal(id: IAffiliateBrandPayoutDestination["id"]) {
    // Return true if default is paypal
    if (
      !id &&
      this.defaultDestination?.code === AffiliatePayoutDestinationCode.PAYPAL
    )
      return true;
    return !!this.payoutDestinations?.find(
      i => i.id === id && i.code === AffiliatePayoutDestinationCode.PAYPAL
    );
  }

  public manageAffiliatePayoutDestination(
    payoutDestination: IAffiliatePayoutDestination | null = null
  ) {
    this.$store.dispatch(
      `data/${DataModules.AFFILIATES_PAYOUT_DESTINATIONS}/openAddEditModal`,
      {
        props: {
          payoutDestination,
          isDisabled: this.$_.isNil(payoutDestination)
            ? !this.canCreate
            : !this.canUpdate,
          createPayoutDestination: this.createPayoutDestination,
          updatePayoutDestination: this.updatePayoutDestination
        },
        events: {
          success: () => this.refreshPayoutDestinations()
        }
      }
    );
  }

  public async deletePayoutDestination(id: IAffiliatePayoutDestination["id"]) {
    if (!this.canDelete) return;

    const modal = await this.$store.dispatch("ui/open/confirmModal", {
      config: {
        parent: this,
        props: {
          autoClose: false,
          confirmButtonText: this.$t("_action.delete"),
          message: this.$t(
            "_sentence.affiliate.delete_payout_destination_confirmation"
          )
        },
        canCancel: [],
        events: {
          confirmed: async () => {
            try {
              await this.$store.dispatch(
                `data/${DataModules.AFFILIATES_PAYOUT_DESTINATIONS}/remove`,
                {
                  brandId: this.brandId,
                  id
                }
              );
              this.refreshPayoutDestinations();
              this.$buefy.toast.open({
                message: this.$t(
                  "_sentence.affiliate.payout_destination_successfully_removed"
                ).toString()
              });
              modal.close();
            } catch (error) {
              modal.$children[0].stopProcessing();
              this.$store.dispatch("api/handleError", error);
            }
          }
        }
      }
    });
  }

  public createPayoutDestination(data) {
    return this.$store.dispatch(
      `data/${DataModules.AFFILIATES_PAYOUT_DESTINATIONS}/create`,
      {
        brandId: this.brandId,
        data
      }
    );
  }

  public updatePayoutDestination(id: IAffiliatePayoutDestination["id"], data) {
    return this.$store.dispatch(
      `data/${DataModules.AFFILIATES_PAYOUT_DESTINATIONS}/update`,
      {
        id,
        brandId: this.brandId,
        data
      }
    );
  }

  public refreshPayoutDestinations() {
    this.getData({ ignoreStored: true });
  }

  // Life-cycle Methods

  public render(h: CreateElement) {
    if (!this.contentIsAvailable)
      return h("guard", { props: { ifAdmin: false } });

    // Handle request error
    if (this.handleRequestError && this.requestError)
      return h("request-error", {
        props: { isLoading: this.table.loading },
        on: { click: () => this.refreshPayoutDestinations() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $affPDData: this.providerData(),
        $affPDMethods: this.providerMethods
      })
    );
  }

  // Provide

  @Provide("affiliatePayoutDestinationsProvider")
  public providerData(): IAffiliatePayoutDestinationsProvider {
    return {
      activeFilters: this.activeFilters,
      canCreate: this.canCreate,
      canDelete: this.canDelete,
      canUpdate: this.canUpdate,
      defaultDestination: this.defaultDestination,
      enableFiltering: this.enableFiltering,
      enablePagination: this.enablePagination,
      enableSorting: this.enableSorting,
      filters: this.filters,
      isLoading: this.table.loading,
      payoutDestinations: this.payoutDestinations,
      sorters: this.sorters,
      table: this.table
    };
  }

  @Provide("affiliatePayoutDestinationsProviderMethods")
  public get providerMethods(): IAffiliatePayoutDestinationsMethods {
    return {
      manageAffiliatePayoutDestination: this.manageAffiliatePayoutDestination,
      deletePayoutDestination: this.deletePayoutDestination,
      createPayoutDestination: this.createPayoutDestination,
      updatePayoutDestination: this.updatePayoutDestination,
      getData: this.getData,
      onFiltersChange: this.onFiltersChange,
      onSort: this.onSort,
      onPageChange: this.onPageChange,
      isPaypal: this.isPaypal
    };
  }
}
